import { ComponentProps } from 'react'
import { v4 as uuidv4 } from 'uuid'

import { pxToRem } from '@/shared/styles/themes'
import { faPlus, faTrashCan } from '@fortawesome/pro-regular-svg-icons'
import { FormHelperText, Grid2 as Grid, styled, Typography } from '@mui/material'
import { GridColDef, GridValidRowModel } from '@mui/x-data-grid-premium'

import { Button } from '../Button/Button'
import { GridTable } from '../GridTable/GridTable'
import { GridRowActionItem } from '../GridTable/models/GridRowActions'
import { Icon } from '../Icon/Icon'
import { OrderItemUpdate } from './models/OrderItemUpdate'
import { OrderItemChangeStateEnum } from './models/OrderItemUpdateState'

export type OrderItemsFieldProps = Pick<ComponentProps<typeof StyledContainer>, 'sx'> & {
  value: OrderItemUpdate[]
  defaultValue?: []
  disabled?: boolean
  error?: boolean
  onChange: (value: OrderItemUpdate[]) => void
}

export const OrderItemsField: React.FC<OrderItemsFieldProps> = ({ value, error, disabled, ...props }) => {
  const columns: GridColDef[] = [
    {
      headerName: 'Description (*)',
      field: 'description',
      filterable: false,
      editable: !disabled,
      width: 200,
    },
    {
      field: 'externalId',
      headerName: 'External Id',
      filterable: false,
      editable: !disabled,
      width: 120,
    },
    {
      field: 'barcode',
      headerName: 'Barcode',
      filterable: false,
      editable: !disabled,
      width: 120,
    },
    {
      field: 'weight',
      headerName: 'Weight',
      filterable: false,
      editable: !disabled,
      width: 120,
    },
    {
      field: 'volume',
      headerName: 'Volume',
      filterable: false,
      editable: !disabled,
      width: 120,
    },
    {
      field: 'quantity',
      headerName: 'Quantity',
      filterable: false,
      editable: !disabled,
      width: 120,
    },
    {
      field: 'unitOfMeasure',
      headerName: 'UoM',
      filterable: false,
      editable: !disabled,
      width: 70,
    },
    {
      field: 'comment',
      headerName: 'Comment',
      filterable: false,
      editable: !disabled,
      width: 200,
    },
  ]

  const onAddOrderItemRow = () => {
    const newRow: OrderItemUpdate = {
      id: uuidv4(),
      description: '',
      state: OrderItemChangeStateEnum.Added,
      isDataValid: false,
      externalId: '',
      barcode: '',
      weight: 0,
      volume: 0,
      quantity: 0,
      unitOfMeasure: '',
      comment: '',
      hasActions: true,
    }

    props.onChange([...value, newRow])
  }

  const onEditRow = (newRow: OrderItemUpdate) => {
    const updatedOrderItemUpdates = value.map((row) => {
      if (row.id === newRow.id) {
        if (newRow.description !== '') {
          newRow.isDataValid = true
        }

        if (newRow.state === OrderItemChangeStateEnum.Unchanged) {
          newRow.state = OrderItemChangeStateEnum.Modified
        }

        return newRow
      }

      return row
    })

    props.onChange(updatedOrderItemUpdates)
  }

  const removeOrderItemAction: GridRowActionItem = {
    icon: faTrashCan,
    label: 'Remove Order Item',
    disabled: disabled,
    onClick: (rowData: GridValidRowModel) => {
      if (rowData.state === OrderItemChangeStateEnum.Added) {
        props.onChange(value.filter((row) => row.id !== rowData.id))
      } else {
        const updatedOrderItemUpdates = value.map((row) => {
          if (row.id === rowData.id) {
            row.state = OrderItemChangeStateEnum.Deleted
          }

          return row
        })

        props.onChange(updatedOrderItemUpdates)
      }
    },
  }

  return (
    <StyledContainer>
      <Grid container direction="row" justifyContent="space-between">
        <Grid>
          <Typography
            variant="body1"
            sx={{
              fontSize: pxToRem(16),
              fontWeight: 500,
              mb: 1.5,
            }}
          >
            Order Items
          </Typography>
        </Grid>
        <Grid>
          <Button
            round
            text
            disabled={disabled}
            startIcon={<Icon icon={faPlus} />}
            onClick={onAddOrderItemRow}
          >
            Add Item
          </Button>
        </Grid>
      </Grid>
      <GridTable
        columns={columns}
        rows={value.filter((row) => row.state !== OrderItemChangeStateEnum.Deleted)}
        slots={{
          footer: () => null,
        }}
        hotAction={removeOrderItemAction}
        processRowUpdate={(newRow, _) => {
          onEditRow(newRow as OrderItemUpdate)

          return newRow
        }}
      />
      <StyledHelperText error={error}>
        {error ? 'Please fill in all required order item fields' : 'Double-click cell to update value'}
      </StyledHelperText>
    </StyledContainer>
  )
}

const StyledContainer = styled('div')`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: ${(props) => props.theme.typography.pxToRem(240)};
`

const StyledHelperText = styled(FormHelperText)`
  margin-bottom: ${(props) => props.theme.typography.pxToRem(8)};
`
